// Sheets — the composition between markup and export (migrations 059, 061).
//
// A SHEET is what a finished map is: an area (the frame, with an aspect for
// the slide or page it is going onto), which layers were on (and, in the
// Studio, which imagery pair and overlays), a title and a caption, how it is
// DRESSED (the decor: title band, legend, scale bar, north arrow, and the key
// the author writes for their pen colours), and the markup drawn on it.
// Before sheets, the cockpit's markup was one flat pile per user with no area
// and no way out of the screen, while the Studio could export but threw its
// markup away on close. Both surfaces now open, reuse, copy and export sheets
// through this module.
//
// Each surface supplies a SheetHost: how to capture and re-apply its own
// layers, how to stage the frames of an animation (the Studio's A / B /
// basemap, the cockpit's as-of dates on the tile clock), what the provenance
// line says, and what legend it derives (territorial control, the change
// layer). Everything else — the flyout, the frame on the map, saving, export —
// is one implementation.
//
// THE FLYOUT HANGS OFF THE MARKUP BAR. It used to be a pod of its own on the
// far side of the screen from the tools; now it opens under the bar's right
// end (markup.ts mounts the bar, this mounts inside it), so "the markup panel"
// is one thing on both surfaces, and opening the sheet arms the bar. When the
// bar comes down, the flyout goes with it.
//
// A TEMPLATE is a composition kept without a sheet — aspect, decor, export
// format, optionally the layer snapshot — so a control map or a damage sheet
// is set up once and every later one starts from it. Private to the author,
// like sheets and markup. Sheets can also be DRAFTS: exportable, not yet
// written, saved the moment a shape is drawn on them or Save is pressed (so
// opening the Studio does not litter the list with sheets nobody kept).
//
// Export is what is on screen (exporter.ts): the frame is fitted to the view,
// every requested frame is staged, settled, read back and composed with its
// title, legend, scale bar, north arrow and footer, then the view is put back.

import * as maplibregl from 'maplibre-gl';

import { del, get, patch, post } from './api';
import {
  ASPECTS, GIF_MAX_EDGE, aspectDrag, bboxOfRect, captureMap, composeSheet, coordLine, coverAspect,
  deviceCrop, fileSlug, fitAspect, mapSettled, mergeLegend, metersPerCssPx, saveGif, savePng, screenRectOf,
  type AspectKey, type Bbox, type LegendEntry, type Rect,
} from './exporter';
import { toast } from './hud';
import {
  bindMarkupSheet, isMarkupActive, MARKUP_COLORS, markupBarElement, markupCount,
  onMarkupCountChange, onMarkupEditorChange, refreshMarkupRail, setMarkupSheetId,
} from './markup';

export interface SheetFrame { bbox: Bbox; aspect: AspectKey; bearing: number }

/** How a sheet is dressed on export. `entries` is the author's own key —
 *  what each pen colour means on THIS sheet — drawn ahead of whatever the
 *  surface derives (control, the change layer) when `legend` is on. */
export interface SheetDecorState {
  title: boolean;
  legend: boolean;
  scale: boolean;
  northArrow: boolean;
  /** The centre and extent in degrees, a line under the footer. */
  coords: boolean;
  entries: LegendEntry[];
}

export const DEFAULT_DECOR: SheetDecorState = {
  title: true, legend: true, scale: true, northArrow: false, coords: true, entries: [],
};

export interface SheetDto {
  id: string;
  title: string;
  caption: string | null;
  surface: 'map' | 'studio';
  isDefault: boolean;
  frame: SheetFrame | null;
  layers: Record<string, unknown> | null;
  imagery: Record<string, unknown> | null;
  /** The server keeps this as the client's own blob; read through normalizeDecor. */
  decor: Record<string, unknown> | null;
  markupCount: number;
  createdAt: string;
  updatedAt: string;
}

/** The sheet a surface is working on: saved (an id) or a draft not yet written. */
export type SheetState = Omit<SheetDto, 'id' | 'decor'> & { id: string | null; decor: SheetDecorState };

export type TemplateSurface = 'any' | 'map' | 'studio';

/** What a template carries: everything about a sheet but its ground and its
 *  shapes. Every field optional, so an older template still applies. */
export interface TemplateSpec {
  aspect?: AspectKey;
  decor?: SheetDecorState;
  format?: 'png' | 'gif';
  fps?: number;
  layers?: Record<string, unknown> | null;
}

export interface TemplateDto {
  id: string;
  name: string;
  surface: TemplateSurface;
  spec: TemplateSpec;
  createdAt: string;
  updatedAt: string;
}

/** `on` is the default: a frame the host adds later (a scene added to the
 *  Studio's series) starts as it says until the author ticks it. */
export interface ExportFrameDef { key: string; label: string; on: boolean }

export interface SheetHost {
  surface: 'map' | 'studio';
  map: maplibregl.Map;
  /** Where the flyout mounts when no markup bar is up. The frame on the map
   *  always goes in the map's own container. */
  mount: HTMLElement;
  /** The frames an animation can step through, in order (the Studio's series
   *  in time order, then the basemap). Read on every render: they change. */
  frames(): ExportFrameDef[];
  /** Whether an export can step through AS-OF DATES instead (the cockpit's tile clock). */
  timeFrames: boolean;
  /** Put the map into a frame's state: a frames() key, `asof:<iso>`, or null for as shown. */
  stage(key: string | null): Promise<void>;
  /** Back to the operator's view after an export. */
  unstage(): Promise<void>;
  /** The provenance line under a frame: dates, platform, attribution. */
  footer(key: string | null): string;
  /** The legend the surface derives for this ground (control, the change layer). */
  legend(bbox: Bbox): LegendEntry[];
  captureLayers(): Record<string, unknown>;
  applyLayers(layers: Record<string, unknown>): void;
  captureImagery(): Record<string, unknown> | null;
  /** "The whole area" — the Studio's scene; absent where there is no such thing. */
  wholeArea?(): Bbox | null;
  /** What that area is called on the buttons ("Image" in the Studio). */
  wholeAreaLabel?: string;
  /** A sheet that belongs on another surface (or another Studio area) was picked. */
  openElsewhere(sheet: SheetDto): void;
  /** Arm the markup bar on this map (synchronously — the flyout mounts in it). */
  startMarkup(): void;
  defaultTitle(): string;
}

type ExportArea = 'frame' | 'view' | 'whole';

/** Per-session export choices that are not part of the sheet. */
interface Session {
  aspect: AspectKey;
  format: 'png' | 'gif';
  fps: number;
}

interface HostState extends Session {
  host: SheetHost;
  sheet: SheetState | null;
  list: SheetDto[];
  templates: TemplateDto[];
  pod: HTMLElement | null;
  mask: HTMLElement | null;
  /** Cancels a frame drag in progress. */
  drawing: (() => void) | null;
  ensuring: Promise<string | null> | null;
  saveTimer: number | undefined;
  exporting: boolean;
  area: ExportArea;
  frameOn: Record<string, boolean>;
  asof: string;
}

const states = new Map<maplibregl.Map, HostState>();
const activeKey = (surface: string): string => `sheet-active-${surface}`;
const LEGEND_LABEL_MAX = 60;

function recall(key: string): string | null {
  try { return localStorage.getItem(key); } catch { return null; }
}
function remember(key: string, value: string): void {
  try { localStorage.setItem(key, value); } catch { /* a convenience */ }
}
function forget(key: string): void {
  try { localStorage.removeItem(key); } catch { /* a convenience */ }
}

const esc = (v: unknown): string => String(v ?? '')
  .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;').replace(/'/g, '&#39;');

// --- pure helpers ------------------------------------------------------------------

/** The export frames an "As of" field asks for, in order: `asof:<iso>` for a
 *  date (a bare day means its END — what was held by the close of it), null
 *  for "now". Throws on a word it cannot read, naming it. */
export function parseAsof(text: string): (string | null)[] {
  const out: (string | null)[] = [];
  for (const raw of text.split(/[\s,;]+/).map((t) => t.trim()).filter(Boolean)) {
    if (raw.toLowerCase() === 'now') { out.push(null); continue; }
    if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) {
      const d = new Date(`${raw}T23:59:59Z`);
      if (!Number.isNaN(d.getTime())) { out.push(`asof:${d.toISOString()}`); continue; }
    } else {
      const d = new Date(raw);
      if (/^\d{4}-\d{2}-\d{2}T/.test(raw) && !Number.isNaN(d.getTime())) {
        out.push(`asof:${d.toISOString()}`);
        continue;
      }
    }
    throw new Error(`“${raw}” is not a date — use YYYY-MM-DD, or “now”.`);
  }
  return out;
}

/** A frame's size on the ground, in words: "16:9 slide · 120 × 68 km". */
export function frameWords(frame: SheetFrame): string {
  const [w, s, e, n] = frame.bbox;
  const midLat = ((s + n) / 2) * (Math.PI / 180);
  const km = (v: number): string => (v >= 100 ? String(Math.round(v)) : v.toFixed(1));
  const wide = (e - w) * 111.32 * Math.cos(midLat);
  const tall = (n - s) * 110.574;
  return `${ASPECTS[frame.aspect]?.label ?? frame.aspect} · ${km(wide)} × ${km(tall)} km`;
}

const isColor = (v: unknown): v is string => typeof v === 'string' && /^#[0-9a-fA-F]{6}$/.test(v);

/** A decor blob from the server (or a template) as the shape the client
 *  works with: missing switches take the defaults, entries are kept only when
 *  they carry a real colour and words, and never more than a legend holds. */
export function normalizeDecor(raw: unknown): SheetDecorState {
  const r = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>;
  const flag = (k: keyof SheetDecorState): boolean =>
    (typeof r[k] === 'boolean' ? r[k] as boolean : DEFAULT_DECOR[k] as boolean);
  const entries: LegendEntry[] = [];
  if (Array.isArray(r.entries)) {
    for (const e of r.entries) {
      if (!e || typeof e !== 'object') continue;
      const { color, label, hatch } = e as Record<string, unknown>;
      const words = typeof label === 'string' ? label.trim().slice(0, LEGEND_LABEL_MAX) : '';
      if (!isColor(color) || !words) continue;
      entries.push({ color: color.toLowerCase(), label: words, ...(hatch === true ? { hatch: true } : {}) });
      if (entries.length === 10) break;
    }
  }
  return { title: flag('title'), legend: flag('legend'), scale: flag('scale'),
           northArrow: flag('northArrow'), coords: flag('coords'), entries };
}

/** A template's spec from a sheet as it is set up: its aspect (from the frame
 *  when there is one), decor, the session's export format, and the layer
 *  snapshot only when the sheet kept one — that was an explicit act. */
export function templateSpecOf(sheet: Pick<SheetState, 'frame' | 'decor' | 'layers'>,
                               session: Session): TemplateSpec {
  const spec: TemplateSpec = {
    aspect: sheet.frame?.aspect ?? session.aspect,
    decor: normalizeDecor(sheet.decor),
    format: session.format,
    fps: session.fps,
  };
  if (sheet.layers) spec.layers = sheet.layers;
  return spec;
}

/** A spec read back from the server, every field checked. */
export function normalizeSpec(raw: unknown): TemplateSpec {
  const r = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>;
  const spec: TemplateSpec = {};
  if (typeof r.aspect === 'string' && r.aspect in ASPECTS) spec.aspect = r.aspect as AspectKey;
  if (r.decor && typeof r.decor === 'object') spec.decor = normalizeDecor(r.decor);
  if (r.format === 'png' || r.format === 'gif') spec.format = r.format;
  if (typeof r.fps === 'number' && r.fps >= 1 && r.fps <= 8) spec.fps = Math.round(r.fps);
  if (r.layers && typeof r.layers === 'object' && !Array.isArray(r.layers)) {
    spec.layers = r.layers as Record<string, unknown>;
  }
  return spec;
}

/** A template applied to a sheet and the session: what the sheet becomes and
 *  what the export choices become. The frame keeps its ground and takes the
 *  template's aspect (the caller re-shapes it on the map); layers are copied
 *  onto the sheet, and it is the caller's job to put them on the map. */
export function applyTemplateSpec<S extends Pick<SheetState, 'frame' | 'decor' | 'layers'>>(
  spec: TemplateSpec, sheet: S, session: Session): { sheet: S; session: Session } {
  const next: S = { ...sheet };
  const out: Session = { ...session };
  if (spec.aspect) {
    out.aspect = spec.aspect;
    if (next.frame) next.frame = { ...next.frame, aspect: spec.aspect };
  }
  if (spec.decor) next.decor = normalizeDecor(spec.decor);
  if (spec.format) out.format = spec.format;
  if (spec.fps) out.fps = spec.fps;
  if (spec.layers) next.layers = spec.layers;
  return { sheet: next, session: out };
}

/** The templates that apply on a surface: its own and the ones for any. */
export const templatesFor = (all: TemplateDto[], surface: 'map' | 'studio'): TemplateDto[] =>
  all.filter((t) => t.surface === 'any' || t.surface === surface);

// --- registering a surface ---------------------------------------------------------

export function registerSheetHost(host: SheetHost): void {
  const st: HostState = {
    host, sheet: null, list: [], templates: [], pod: null, mask: null, drawing: null,
    ensuring: null, saveTimer: undefined, exporting: false, aspect: 'free', format: 'png',
    area: 'frame', frameOn: {}, asof: '', fps: 2,
  };
  for (const f of host.frames()) st.frameOn[f.key] = f.on;
  states.set(host.map, st);
  const repaint = (): void => paintMask(st);
  host.map.on('move', repaint);
  host.map.on('resize', repaint);
  onMarkupCountChange(host.map, () => syncCount(st));
  // The flyout lives in the bar: when the bar comes down, so does it.
  onMarkupEditorChange(host.map, (open) => { if (!open) closeSheetPod(host.map); });
  host.map.once('remove', () => {
    st.drawing?.();
    window.clearTimeout(st.saveTimer);
    st.pod?.remove();
    st.mask?.remove();
    states.delete(host.map);
  });
}

function toState(sheet: SheetDto): SheetState {
  return { ...sheet, decor: normalizeDecor(sheet.decor) };
}

function draftFor(host: SheetHost): SheetState {
  return {
    id: null, title: host.defaultTitle(), caption: null, surface: host.surface,
    isDefault: false, frame: null, layers: null, imagery: null, decor: { ...DEFAULT_DECOR, entries: [] },
    markupCount: 0, createdAt: '', updatedAt: '',
  };
}

export const activeSheet = (map: maplibregl.Map): SheetState | null => states.get(map)?.sheet ?? null;

/** What the markup bar says shapes are going to. */
export function activeSheetTitle(map: maplibregl.Map): string {
  const s = states.get(map)?.sheet;
  if (!s) return 'My markup';
  return s.id ? s.title : `${s.title} (unsaved)`;
}

/** The cockpit's boot: the sheet it was last working on, else the default
 *  sheet's markup. */
export async function restoreActiveSheet(map: maplibregl.Map): Promise<void> {
  const st = states.get(map);
  const id = st ? recall(activeKey(st.host.surface)) : null;
  if (st && id) {
    try {
      const sheet = await get<SheetDto>(`/api/sheets/${encodeURIComponent(id)}`);
      if (sheet.surface === st.host.surface) {
        await useSheet(map, sheet);
        return;
      }
    } catch {
      forget(activeKey(st.host.surface));
    }
  }
  await bindMarkupSheet(map, null);
}

/** Start an unsaved sheet: nothing drawn yet, written on first use. */
export function startDraft(map: maplibregl.Map, spec?: TemplateSpec): void {
  const st = states.get(map);
  if (!st) return;
  st.sheet = draftFor(st.host);
  st.area = unframedArea(st);
  if (st.host.surface === 'map') forget(activeKey('map'));
  if (spec) applyTemplate(st, spec);
  void bindMarkupSheet(map, null, () => ensureSheetFor(map));
  renderPod(st);
  paintMask(st);
  refreshMarkupRail();
}

/** Work on a saved sheet: its markup loads; `applyLayers` and `fly` also put
 *  its layers and its frame back. */
export async function useSheet(map: maplibregl.Map, sheet: SheetDto,
                               opts: { fly?: boolean; applyLayers?: boolean } = {}): Promise<void> {
  const st = states.get(map);
  if (!st) return;
  st.sheet = toState(sheet);
  st.aspect = sheet.frame?.aspect ?? st.aspect;
  // A sheet with a frame exports it; one without, the image or the view.
  st.area = sheet.frame ? 'frame' : unframedArea(st);
  if (st.host.surface === 'map') remember(activeKey('map'), sheet.id);
  if (!st.list.some((x) => x.id === sheet.id)) st.list = [sheet, ...st.list];
  await bindMarkupSheet(map, sheet.id);
  if (opts.applyLayers && sheet.layers) st.host.applyLayers(sheet.layers);
  if (opts.fly && sheet.frame) flyToFrame(st, sheet.frame, true);
  renderPod(st);
  paintMask(st);
  refreshMarkupRail();
}

/** The active sheet's id, writing a draft first if need be. One write however
 *  many shapes ask for it at once. */
export function ensureSheetFor(map: maplibregl.Map): Promise<string | null> {
  const st = states.get(map);
  if (!st) return Promise.resolve(null);
  if (st.sheet?.id) return Promise.resolve(st.sheet.id);
  if (!st.ensuring) st.ensuring = persist(st).finally(() => { st.ensuring = null; });
  return st.ensuring;
}

async function persist(st: HostState): Promise<string | null> {
  if (!st.sheet) st.sheet = draftFor(st.host);
  if (st.sheet.id) return st.sheet.id;
  const d = st.sheet;
  try {
    const made = await post<SheetDto>('/api/sheets', {
      title: d.title.trim() || st.host.defaultTitle(),
      caption: d.caption?.trim() || null,
      surface: st.host.surface,
      frame: d.frame,
      layers: d.layers ?? st.host.captureLayers(),
      imagery: st.host.captureImagery() ?? d.imagery,
      decor: d.decor,
    });
    const typed = st.sheet;
    st.sheet = { ...toState(made), title: typed.title || made.title, caption: typed.caption,
                 frame: typed.frame ?? made.frame, decor: typed.decor };
    setMarkupSheetId(st.host.map, made.id);
    if (st.host.surface === 'map') remember(activeKey('map'), made.id);
    st.list = [made, ...st.list.filter((x) => x.id !== made.id)];
    // Anything typed while the request was out still has to land.
    if (st.sheet.title !== made.title || (st.sheet.caption ?? null) !== made.caption
        || JSON.stringify(st.sheet.frame) !== JSON.stringify(made.frame)
        || JSON.stringify(st.sheet.decor) !== JSON.stringify(normalizeDecor(made.decor))) {
      queueSave(st, true);
    }
    renderPod(st);
    refreshMarkupRail();
    return made.id;
  } catch (err) {
    toast(err instanceof Error ? err.message : 'Could not save the sheet.');
    return null;
  }
}

function queueSave(st: HostState, now = false): void {
  window.clearTimeout(st.saveTimer);
  if (!st.sheet?.id) return;           // a draft saves when it becomes a sheet
  st.saveTimer = window.setTimeout(() => { void flushSave(st); }, now ? 0 : 600);
}

async function flushSave(st: HostState, fields?: Record<string, unknown>): Promise<void> {
  const s = st.sheet;
  if (!s?.id) return;
  const body = fields ?? {
    title: s.title.trim() || st.host.defaultTitle(),
    caption: s.caption?.trim() || null,
    frame: s.frame,
    decor: s.decor,
  };
  try {
    const updated = await patch<SheetDto>(`/api/sheets/${s.id}`, body);
    if (st.sheet?.id === updated.id) {
      st.sheet.updatedAt = updated.updatedAt;
      st.sheet.layers = updated.layers;
      st.sheet.imagery = updated.imagery;
    }
    st.list = st.list.map((x) => (x.id === updated.id ? updated : x));
    renderPicker(st);
    renderState(st);
  } catch (err) {
    toast(err instanceof Error ? err.message : 'Could not save the sheet.');
  }
}

async function refreshList(st: HostState): Promise<void> {
  try {
    st.list = (await get<{ sheets: SheetDto[] }>('/api/sheets')).sheets;
  } catch {
    return;
  }
  renderPicker(st);
}

async function refreshTemplates(st: HostState): Promise<void> {
  try {
    const got = (await get<{ templates: TemplateDto[] }>('/api/sheet-templates')).templates;
    st.templates = got.map((t) => ({ ...t, spec: normalizeSpec(t.spec) }));
  } catch {
    return;
  }
  renderTemplates(st);
}

// --- the frame on the map ----------------------------------------------------------

/** Where an export goes when there is no frame: the host's whole area (the
 *  Studio's loaded image — what a Studio export is nearly always of), else
 *  the view. */
const unframedArea = (st: HostState): ExportArea => (st.host.wholeArea ? 'whole' : 'view');
const wholeWord = (st: HostState): string => st.host.wholeAreaLabel ?? 'Whole area';

function flyToFrame(st: HostState, frame: SheetFrame, animate: boolean): void {
  const [w, s, e, n] = frame.bbox;
  st.host.map.fitBounds([[w, s], [e, n]],
                        { padding: 40, bearing: frame.bearing, duration: animate ? 600 : 0 });
}

function setFrame(st: HostState, frame: SheetFrame | null): void {
  if (!st.sheet) return;
  st.sheet.frame = frame;
  if (frame) st.aspect = frame.aspect;
  if (!frame && st.area === 'frame') st.area = unframedArea(st);
  // Drawing or fitting a frame is saying "export this".
  if (frame) st.area = 'frame';
  paintMask(st);
  renderPod(st);
  queueSave(st, true);
}

/** Re-shape the frame in place to the session's aspect: the largest frame of
 *  the new shape that fits inside the old one. */
function reshapeFrame(st: HostState): void {
  const frame = st.sheet?.frame;
  if (!frame) return;
  const map = st.host.map;
  const r = fitAspect(screenRectOf((ll) => map.project(ll), frame.bbox), ASPECTS[st.aspect].ratio);
  setFrame(st, { bbox: bboxOfRect((p) => map.unproject(p), r), aspect: st.aspect,
                 bearing: frame.bearing });
}

/** The frame, drawn over the map while the flyout is open: everything
 *  outside it dimmed, so what will be exported is what is bright. */
function paintMask(st: HostState): void {
  const frame = st.pod ? st.sheet?.frame : null;
  if (!frame) {
    st.mask?.remove();
    st.mask = null;
    return;
  }
  const map = st.host.map;
  if (!st.mask) {
    st.mask = document.createElement('div');
    st.mask.className = 'sheet-frame';
    st.mask.innerHTML = '<span class="sf-label"></span>';
    map.getContainer().appendChild(st.mask);
  }
  const r = screenRectOf((ll) => map.project(ll), frame.bbox);
  Object.assign(st.mask.style, {
    left: `${r.x}px`, top: `${r.y}px`, width: `${r.w}px`, height: `${r.h}px`,
  });
  (st.mask.firstElementChild as HTMLElement).textContent = frameWords(frame);
}

function beginFrameDraw(st: HostState): void {
  st.drawing?.();
  const map = st.host.map;
  const layer = document.createElement('div');
  layer.className = 'sheet-draw';
  const band = document.createElement('div');
  band.className = 'sd-band';
  band.hidden = true;
  layer.appendChild(band);
  map.getContainer().appendChild(layer);
  const ratio = ASPECTS[st.aspect].ratio;
  toast(`Drag the frame${ratio ? ` (${ASPECTS[st.aspect].label})` : ''} on the map. Esc cancels.`);
  let start: { x: number; y: number } | null = null;
  const at = (ev: PointerEvent): { x: number; y: number } => {
    const b = layer.getBoundingClientRect();
    return { x: ev.clientX - b.left, y: ev.clientY - b.top };
  };
  const show = (r: Rect): void => {
    band.hidden = false;
    Object.assign(band.style, { left: `${r.x}px`, top: `${r.y}px`, width: `${r.w}px`, height: `${r.h}px` });
  };
  const stop = (): void => {
    layer.remove();
    if (st.drawing === stop) st.drawing = null;
  };
  // Pointer events, not MapLibre's mouse events: this has to work on a phone.
  layer.addEventListener('pointerdown', (ev) => {
    ev.preventDefault();
    ev.stopPropagation();
    start = at(ev);
    layer.setPointerCapture(ev.pointerId);
  });
  layer.addEventListener('pointermove', (ev) => { if (start) show(aspectDrag(start, at(ev), ratio)); });
  layer.addEventListener('pointerup', (ev) => {
    if (!start) return;
    const r = aspectDrag(start, at(ev), ratio);
    start = null;
    // A stray tap would otherwise set a one-pixel frame and a sliver export.
    if (r.w < 24 || r.h < 24) {
      band.hidden = true;
      toast('That frame is too small — drag a larger one.');
      return;
    }
    stop();
    setFrame(st, { bbox: bboxOfRect((p) => map.unproject(p), r), aspect: st.aspect,
                   bearing: map.getBearing() });
  });
  st.drawing = stop;
}

// --- the flyout ------------------------------------------------------------------

/** Open the sheet flyout on `map`, arming the markup bar it hangs off first.
 *  `focus` scrolls a section into view. */
export async function openSheetPod(map: maplibregl.Map,
                                   focus: 'export' | 'templates' | null = null): Promise<void> {
  const st = states.get(map);
  if (!st) return;
  if (!isMarkupActive(map)) st.host.startMarkup();
  if (!st.sheet) await resolveInitial(st);
  if (!st.pod) buildPod(st);
  renderPod(st);
  paintMask(st);
  refreshMarkupRail();
  void refreshList(st);
  void refreshTemplates(st);
  if (focus) {
    st.pod?.querySelector(`[data-sec="${focus}"]`)?.scrollIntoView({ block: 'start' });
  }
}

/** Which sheet a flyout opened with nothing active shows: in the Studio, a
 *  draft; on the cockpit, the default sheet its markup is already on, else a
 *  draft. */
async function resolveInitial(st: HostState): Promise<void> {
  if (st.host.surface === 'map') {
    try {
      st.list = (await get<{ sheets: SheetDto[] }>('/api/sheets')).sheets;
    } catch {
      st.list = [];
    }
    const def = st.list.find((x) => x.isDefault && x.surface === 'map');
    if (def) { await useSheet(st.host.map, def); return; }
  }
  startDraft(st.host.map);
}

export function closeSheetPod(map: maplibregl.Map): boolean {
  const st = states.get(map);
  if (!st?.pod) return false;
  st.drawing?.();
  st.pod.remove();
  st.pod = null;
  paintMask(st);
  refreshMarkupRail();
  return true;
}

export const isSheetPodOpen = (map: maplibregl.Map): boolean => !!states.get(map)?.pod;

/** The bar's Sheet button: open the flyout, or close it if it is up. */
export function toggleSheetPod(map: maplibregl.Map): void {
  if (!closeSheetPod(map)) void openSheetPod(map);
}

/** Cancel a frame drag in progress. True when there was one. */
export function cancelFrameDraw(map: maplibregl.Map): boolean {
  const st = states.get(map);
  if (!st?.drawing) return false;
  st.drawing();
  return true;
}

/** Esc: a frame drag first, the flyout second. True when it consumed the key. */
export function sheetEscape(map: maplibregl.Map): boolean {
  const st = states.get(map);
  if (!st) return false;
  if (st.drawing) { st.drawing(); return true; }
  return closeSheetPod(map);
}

/** The bar's Export button: export the sheet as it is set up, in one press.
 *  When it cannot, the flyout opens at Export and says why. */
export async function exportActiveSheet(map: maplibregl.Map): Promise<void> {
  const st = states.get(map);
  if (!st) return;
  if (!st.sheet) await resolveInitial(st);
  const plan = exportPlan(st);
  if (plan.problem) {
    await openSheetPod(map, 'export');
    toast(plan.problem);
    return;
  }
  await runExport(st);
}

function buildPod(st: HostState): void {
  const host = st.host;
  const map = host.map;
  const pod = document.createElement('div');
  const bar = markupBarElement(map);
  pod.className = `pod sheet-fly${bar ? ' in-bar' : ''}${host.surface === 'studio' ? ' in-studio' : ''}`;
  const aspects = (Object.keys(ASPECTS) as AspectKey[])
    .map((k) => `<option value="${k}">${ASPECTS[k].label}</option>`).join('');
  const frames = host.frames();
  const swatches = MARKUP_COLORS.map((c) => `<label class="sh-key">
      <span class="sh-sw" style="background:${c}"></span>
      <input data-key="${c}" maxlength="${LEGEND_LABEL_MAX}" placeholder="what ${c === '#ffffff' ? 'white' : 'this colour'} means">
    </label>`).join('');
  pod.innerHTML = `
    <header class="sh-head">
      <select data-sh="pick" title="Open another sheet"></select>
      <button type="button" data-sh="new" title="Start a new sheet">＋</button>
      <button type="button" class="sh-x" data-sh="close" title="Close (Esc)">✕</button>
    </header>
    <div class="sh-body">
      <label class="sh-f">Title <input data-sh="title" maxlength="120"></label>
      <label class="sh-f">Caption <textarea data-sh="caption" rows="2" maxlength="2000"
        placeholder="What the map shows — printed under the title"></textarea></label>
      <div class="sh-row"><small class="sh-note sh-grow" data-sh="state"></small>
        <button type="button" data-sh="save" title="Keep this sheet">Save sheet</button></div>

      <div class="sh-sec">Frame</div>
      <div class="sh-row">
        <select data-sh="aspect" title="The shape of the finished map">${aspects}</select>
        <button type="button" data-sh="fitview" title="Frame what is on screen">Use view</button>
        ${host.wholeArea ? `<button type="button" data-sh="fitwhole" title="A frame of this shape around the ${esc(wholeWord(st).toLowerCase())}">Fit to ${esc(wholeWord(st).toLowerCase())}</button>` : ''}
        <button type="button" data-sh="drawframe" title="Drag the frame on the map">Draw</button>
      </div>
      <div class="sh-row">
        <button type="button" data-sh="goto" title="Go to the frame">Go to</button>
        <button type="button" data-sh="clearframe" title="No frame: export the view">Clear</button>
        <small class="sh-note sh-grow" data-sh="framenote"></small>
      </div>

      <div class="sh-sec">${host.surface === 'studio' ? 'Imagery and overlays' : 'Layers'}</div>
      <div class="sh-row">
        <button type="button" data-sh="savelayers" title="Remember what is showing now">Save current</button>
        <button type="button" data-sh="applylayers" title="Put the saved layers back">Apply saved</button>
      </div>
      <small class="sh-note" data-sh="layersnote"></small>

      <div class="sh-sec" data-sec="composition">Composition</div>
      <div class="sh-row">
        <label class="sh-chk"><input type="checkbox" data-dec="title"> Title</label>
        <label class="sh-chk"><input type="checkbox" data-dec="legend"> Legend</label>
        <label class="sh-chk"><input type="checkbox" data-dec="scale"> Scale bar</label>
        <label class="sh-chk"><input type="checkbox" data-dec="northArrow"> North arrow</label>
        <label class="sh-chk" title="The centre and the extent of the image in degrees (WGS 84), a line under the footer"><input type="checkbox" data-dec="coords"> Coordinates</label>
      </div>
      <details class="sh-keys" data-sh="keys">
        <summary title="Name what each pen colour means on this sheet — it is printed in the legend">Key <small class="sh-note" data-sh="keycount"></small></summary>
        <div class="sh-keylist">${swatches}</div>
        <small class="sh-note">${host.surface === 'studio'
          ? 'Printed ahead of the change-evidence entry.'
          : 'Printed ahead of the territorial control inside the frame.'}</small>
      </details>

      <div class="sh-sec" data-sec="templates">Templates</div>
      <div class="sh-row">
        <select data-sh="tpl" class="sh-grow" title="A composition kept for reuse: aspect, furniture, key, format"></select>
      </div>
      <div class="sh-row">
        <button type="button" data-sh="tplapply" title="Set this sheet up like the template">Apply</button>
        <button type="button" data-sh="tplnew" title="Start a new sheet set up like the template">New sheet from it</button>
        <button type="button" data-sh="tplsave" title="Keep this sheet's set-up as a template">Save as…</button>
        <button type="button" data-sh="tpldelete" title="Delete the template">✕</button>
      </div>
      <small class="sh-note" data-sh="tplnote"></small>

      <div class="sh-sec" data-sec="export">Export</div>
      <div class="sh-row sh-seg">
        <button type="button" data-fmt="png">Still PNG</button>
        <button type="button" data-fmt="gif">Animated GIF</button>
      </div>
      <div class="sh-row sh-seg">
        <button type="button" data-area="frame" title="Exactly the frame">Frame</button>
        <button type="button" data-area="view" title="Exactly what is on screen">View</button>
        ${host.wholeArea ? `<button type="button" data-area="whole" title="Exactly the ${esc(wholeWord(st).toLowerCase())}, however the map is framed">${esc(wholeWord(st))}</button>` : ''}
      </div>
      ${frames.length ? '<div class="sh-row" data-gif-only data-frames></div>' : ''}
      ${host.timeFrames ? `<label class="sh-f">As of <input data-sh="asof"
        placeholder="2026-06-01, 2026-09-14, now"
        title="Territorial control and mapped objects as they stood — one date for a still, several for an animation"></label>` : ''}
      <div class="sh-row" data-gif-only>
        <label class="sh-chk">Speed <input type="range" data-sh="fps" min="1" max="8" step="1"></label>
        <small class="sh-note" data-sh="fpsval"></small>
      </div>
      <div class="sh-row">
        <small class="sh-note sh-grow" data-sh="exnote"></small>
        <button type="button" class="commit" data-sh="export">↓ Export</button>
      </div>
      <div class="sh-row sh-manage">
        <button type="button" data-sh="copy" title="A copy of this sheet, markup and all">Copy</button>
        <button type="button" data-sh="delete" title="Delete this sheet and its markup">Delete</button>
      </div>
    </div>`;
  (bar ?? host.mount).appendChild(pod);
  st.pod = pod;

  // The flyout sits over the map: a press that reached the canvas would pan
  // it, or drop a shape where the operator aimed at a button.
  for (const type of ['pointerdown', 'mousedown', 'touchstart', 'wheel']) {
    pod.addEventListener(type, (ev) => ev.stopPropagation());
  }
  // And a click inside it must not reach the bar's own handler.
  pod.addEventListener('click', (ev) => ev.stopPropagation());
  const q = <T extends HTMLElement>(sel: string): T | null => pod.querySelector(sel) as T | null;

  q<HTMLSelectElement>('[data-sh="pick"]')!.addEventListener('change', (ev) => {
    const id = (ev.target as HTMLSelectElement).value;
    const sheet = st.list.find((x) => x.id === id);
    if (!sheet || sheet.id === st.sheet?.id) return;
    // A Studio sheet belongs to its own imagery area, so another one is opened
    // afresh rather than swapped into this Studio.
    if (sheet.surface !== host.surface || host.surface === 'studio') host.openElsewhere(sheet);
    else void useSheet(map, sheet, { fly: true, applyLayers: true });
  });
  q('[data-sh="new"]')!.addEventListener('click', () => {
    startDraft(map);
    toast('New sheet — it is saved when you draw on it or press Save.');
  });
  q('[data-sh="close"]')!.addEventListener('click', () => { closeSheetPod(map); });
  q<HTMLInputElement>('[data-sh="title"]')!.addEventListener('input', (ev) => {
    if (!st.sheet) return;
    st.sheet.title = (ev.target as HTMLInputElement).value;
    queueSave(st);
    renderPicker(st);
    paintMask(st);
    refreshMarkupRail();
  });
  q<HTMLTextAreaElement>('[data-sh="caption"]')!.addEventListener('input', (ev) => {
    if (!st.sheet) return;
    st.sheet.caption = (ev.target as HTMLTextAreaElement).value;
    queueSave(st);
  });
  q('[data-sh="save"]')!.addEventListener('click', () => {
    void ensureSheetFor(map).then((id) => { if (id) toast('Sheet saved.'); });
  });
  q<HTMLSelectElement>('[data-sh="aspect"]')!.addEventListener('change', (ev) => {
    st.aspect = (ev.target as HTMLSelectElement).value as AspectKey;
    if (st.sheet?.frame) reshapeFrame(st);
    else renderPod(st);
  });
  q('[data-sh="fitview"]')!.addEventListener('click', () => {
    const c = map.getContainer();
    const inset = { x: c.clientWidth * 0.06, y: c.clientHeight * 0.06,
                    w: c.clientWidth * 0.88, h: c.clientHeight * 0.88 };
    const r = fitAspect(inset, ASPECTS[st.aspect].ratio);
    setFrame(st, { bbox: bboxOfRect((p) => map.unproject(p), r), aspect: st.aspect,
                   bearing: map.getBearing() });
  });
  q('[data-sh="fitwhole"]')?.addEventListener('click', () => {
    const whole = host.wholeArea?.();
    if (!whole) return;
    const r = coverAspect(screenRectOf((ll) => map.project(ll), whole), ASPECTS[st.aspect].ratio);
    setFrame(st, { bbox: bboxOfRect((p) => map.unproject(p), r), aspect: st.aspect,
                   bearing: map.getBearing() });
  });
  q('[data-sh="drawframe"]')!.addEventListener('click', () => beginFrameDraw(st));
  q('[data-sh="goto"]')!.addEventListener('click', () => {
    if (st.sheet?.frame) flyToFrame(st, st.sheet.frame, true);
  });
  q('[data-sh="clearframe"]')!.addEventListener('click', () => setFrame(st, null));
  q('[data-sh="savelayers"]')!.addEventListener('click', () => {
    if (!st.sheet) return;
    const layers = host.captureLayers();
    const imagery = host.captureImagery();
    st.sheet.layers = layers;
    if (imagery) st.sheet.imagery = imagery;
    if (st.sheet.id) void flushSave(st, imagery ? { layers, imagery } : { layers });
    renderPod(st);
    toast(host.surface === 'studio' ? 'Imagery and overlays saved on the sheet.' : 'Layers saved on the sheet.');
  });
  q('[data-sh="applylayers"]')!.addEventListener('click', () => {
    if (st.sheet?.layers) host.applyLayers(st.sheet.layers);
  });

  // Composition: every change is the sheet changing.
  pod.querySelectorAll<HTMLInputElement>('[data-dec]').forEach((cb) => cb.addEventListener('change', () => {
    if (!st.sheet) return;
    const key = cb.dataset.dec as keyof Omit<SheetDecorState, 'entries'>;
    st.sheet.decor = { ...st.sheet.decor, [key]: cb.checked };
    queueSave(st);
    renderExportNote(st);
  }));
  pod.querySelectorAll<HTMLInputElement>('[data-key]').forEach((inp) => inp.addEventListener('input', () => {
    if (!st.sheet) return;
    st.sheet.decor = { ...st.sheet.decor, entries: readKeys(pod) };
    syncKeyCount(st);
    queueSave(st);
  }));

  // Templates.
  q('[data-sh="tplapply"]')!.addEventListener('click', () => {
    const t = pickedTemplate(st);
    if (!t) return;
    applyTemplate(st, t.spec);
    toast(`Set up like “${t.name}”.`);
  });
  q('[data-sh="tplnew"]')!.addEventListener('click', () => {
    const t = pickedTemplate(st);
    if (!t) return;
    startDraft(map, t.spec);
    toast(`New sheet from “${t.name}” — saved when you draw on it or press Save.`);
  });
  q('[data-sh="tplsave"]')!.addEventListener('click', () => { void saveTemplate(st); });
  q('[data-sh="tpldelete"]')!.addEventListener('click', () => { void deleteTemplate(st); });
  q<HTMLSelectElement>('[data-sh="tpl"]')!.addEventListener('change', () => renderTemplates(st));

  pod.querySelectorAll<HTMLButtonElement>('[data-fmt]').forEach((b) => b.addEventListener('click', () => {
    st.format = b.dataset.fmt as 'png' | 'gif';
    renderPod(st);
  }));
  pod.querySelectorAll<HTMLButtonElement>('[data-area]').forEach((b) => b.addEventListener('click', () => {
    st.area = b.dataset.area as ExportArea;
    renderPod(st);
  }));
  pod.querySelector('[data-frames]')?.addEventListener('change', (ev) => {
    const cb = (ev.target as HTMLElement).closest<HTMLInputElement>('[data-frame]');
    if (!cb) return;
    st.frameOn[cb.dataset.frame!] = cb.checked;
    renderExportNote(st);
  });
  q<HTMLInputElement>('[data-sh="asof"]')?.addEventListener('input', (ev) => {
    st.asof = (ev.target as HTMLInputElement).value;
    renderExportNote(st);
  });
  q<HTMLInputElement>('[data-sh="fps"]')!.addEventListener('input', (ev) => {
    st.fps = Number((ev.target as HTMLInputElement).value);
    renderPod(st);
  });
  q('[data-sh="export"]')!.addEventListener('click', () => { void runExport(st); });
  q('[data-sh="copy"]')!.addEventListener('click', () => { void copySheet(st); });
  q('[data-sh="delete"]')!.addEventListener('click', () => { void deleteSheet(st); });
}

/** The key as typed: one entry per pen colour with words. */
function readKeys(pod: HTMLElement): LegendEntry[] {
  const out: LegendEntry[] = [];
  pod.querySelectorAll<HTMLInputElement>('[data-key]').forEach((inp) => {
    const label = inp.value.trim().slice(0, LEGEND_LABEL_MAX);
    if (label) out.push({ color: inp.dataset.key!.toLowerCase(), label });
  });
  return out;
}

function syncKeyCount(st: HostState): void {
  const el = st.pod?.querySelector('[data-sh="keycount"]') as HTMLElement | null;
  if (!el || !st.sheet) return;
  const n = st.sheet.decor.entries.length;
  el.textContent = n ? `· ${n} named` : '· name your colours';
}

function renderPicker(st: HostState): void {
  const sel = st.pod?.querySelector('[data-sh="pick"]') as HTMLSelectElement | null;
  if (!sel || !st.sheet) return;
  const current = st.sheet;
  const where = (s: { surface: string }): string =>
    (s.surface === st.host.surface ? '' : s.surface === 'studio' ? ' · Studio' : ' · map');
  const options = current.id ? [] : [`<option value="" selected>${esc(current.title || 'Untitled')} (unsaved)</option>`];
  for (const s of st.list) {
    const title = s.id === current.id ? current.title : s.title;
    options.push(`<option value="${esc(s.id)}"${s.id === current.id ? ' selected' : ''}>${
      esc(title || 'Untitled')}${where(s)}</option>`);
  }
  sel.innerHTML = options.join('');
}

function renderState(st: HostState): void {
  const el = st.pod?.querySelector('[data-sh="state"]') as HTMLElement | null;
  if (!el || !st.sheet) return;
  const n = markupCount(st.host.map);
  const shapes = n ? `${n} shape${n === 1 ? '' : 's'}` : 'nothing drawn yet';
  el.textContent = st.sheet.id
    ? `Saved${st.sheet.updatedAt ? ` ${new Date(st.sheet.updatedAt).toLocaleString(undefined,
        { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}` : ''} · ${shapes}`
    : `Unsaved — kept once you draw on it or press Save · ${shapes}`;
  (st.pod!.querySelector('[data-sh="save"]') as HTMLElement).hidden = !!st.sheet.id;
}

/** The markup count changed: it is on the state line. */
function syncCount(st: HostState): void {
  renderState(st);
}

function pickedTemplate(st: HostState): TemplateDto | null {
  const sel = st.pod?.querySelector('[data-sh="tpl"]') as HTMLSelectElement | null;
  if (!sel) return null;
  return st.templates.find((t) => t.id === sel.value) ?? null;
}

function renderTemplates(st: HostState): void {
  const pod = st.pod;
  if (!pod) return;
  const sel = pod.querySelector('[data-sh="tpl"]') as HTMLSelectElement;
  const mine = templatesFor(st.templates, st.host.surface);
  const keep = sel.value;
  sel.innerHTML = mine.length
    ? mine.map((t) => `<option value="${esc(t.id)}">${esc(t.name)}${t.surface === 'any' ? '' : ` · ${t.surface === 'studio' ? 'Studio' : 'map'}`}</option>`).join('')
    : '<option value="">No templates yet</option>';
  if (mine.some((t) => t.id === keep)) sel.value = keep;
  const picked = pickedTemplate(st);
  for (const key of ['tplapply', 'tplnew', 'tpldelete']) {
    (pod.querySelector(`[data-sh="${key}"]`) as HTMLButtonElement).disabled = !picked;
  }
  const note = pod.querySelector('[data-sh="tplnote"]') as HTMLElement;
  if (!picked) {
    note.textContent = 'Save as… keeps this sheet’s aspect, furniture, key and format for the next one.';
    return;
  }
  const s = picked.spec;
  const parts: string[] = [];
  if (s.aspect) parts.push(ASPECTS[s.aspect].label);
  if (s.decor) {
    const on = (['title', 'legend', 'scale', 'northArrow', 'coords'] as const).filter((k) => s.decor![k]);
    const words: Record<string, string> = { northArrow: 'north arrow', scale: 'scale bar', coords: 'coordinates' };
    parts.push(on.length ? on.map((k) => words[k] ?? k).join(', ') : 'no furniture');
    if (s.decor.entries.length) parts.push(`${s.decor.entries.length}-entry key`);
  }
  if (s.format) parts.push(s.format.toUpperCase());
  if (s.layers) parts.push('layers');
  note.textContent = parts.join(' · ');
}

/** Write the state into the flyout. Never overwrites the field being typed in. */
function renderPod(st: HostState): void {
  const pod = st.pod;
  const s = st.sheet;
  if (!pod || !s) return;
  const setValue = (sel: string, v: string): void => {
    const el = pod.querySelector(sel) as HTMLInputElement | HTMLTextAreaElement | null;
    if (el && document.activeElement !== el && el.value !== v) el.value = v;
  };
  setValue('[data-sh="title"]', s.title);
  setValue('[data-sh="caption"]', s.caption ?? '');
  renderPicker(st);
  renderState(st);
  (pod.querySelector('[data-sh="aspect"]') as HTMLSelectElement).value = st.aspect;
  (pod.querySelector('[data-sh="framenote"]') as HTMLElement).textContent =
    s.frame ? frameWords(s.frame) : 'No frame — exports use the view';
  for (const sel of ['[data-sh="goto"]', '[data-sh="clearframe"]']) {
    (pod.querySelector(sel) as HTMLButtonElement).disabled = !s.frame;
  }
  (pod.querySelector('[data-sh="applylayers"]') as HTMLButtonElement).disabled = !s.layers;
  (pod.querySelector('[data-sh="layersnote"]') as HTMLElement).textContent = s.layers
    ? 'This sheet remembers its layers — Apply puts them back.'
    : 'Nothing saved yet — Save current remembers what is on now.';
  pod.querySelectorAll<HTMLInputElement>('[data-dec]').forEach((cb) => {
    cb.checked = !!s.decor[cb.dataset.dec as keyof SheetDecorState];
  });
  const byColor = new Map(s.decor.entries.map((e) => [e.color.toLowerCase(), e.label]));
  pod.querySelectorAll<HTMLInputElement>('[data-key]').forEach((inp) => {
    setValue(`[data-key="${inp.dataset.key}"]`, byColor.get(inp.dataset.key!.toLowerCase()) ?? '');
  });
  syncKeyCount(st);
  renderTemplates(st);
  syncCount(st);
  if (st.area === 'frame' && !s.frame) st.area = unframedArea(st);
  pod.querySelectorAll<HTMLElement>('[data-fmt]').forEach((b) =>
    b.classList.toggle('on', b.dataset.fmt === st.format));
  pod.querySelectorAll<HTMLButtonElement>('[data-area]').forEach((b) => {
    b.classList.toggle('on', b.dataset.area === st.area);
    if (b.dataset.area === 'frame') b.disabled = !s.frame;
  });
  pod.querySelectorAll<HTMLElement>('[data-gif-only]').forEach((el) => { el.hidden = st.format !== 'gif'; });
  renderFrames(st);
  setValue('[data-sh="asof"]', st.asof);
  (pod.querySelector('[data-sh="fps"]') as HTMLInputElement).value = String(st.fps);
  (pod.querySelector('[data-sh="fpsval"]') as HTMLElement).textContent = `${st.fps} / sec`;
  (pod.querySelector('[data-sh="copy"]') as HTMLButtonElement).disabled = !s.id;
  (pod.querySelector('[data-sh="delete"]') as HTMLButtonElement).disabled = !s.id;
  renderExportNote(st);
}

const frameIsOn = (st: HostState, f: ExportFrameDef): boolean => st.frameOn[f.key] ?? f.on;

/** The frame ticks, rebuilt from the host each render: the Studio's series
 *  grows and shrinks while the flyout is open. */
function renderFrames(st: HostState): void {
  const row = st.pod?.querySelector<HTMLElement>('[data-frames]');
  if (!row) return;
  row.innerHTML = st.host.frames().map((f) =>
    `<label class="sh-chk"><input type="checkbox" data-frame="${esc(f.key)}"${frameIsOn(st, f) ? ' checked' : ''}> ${esc(f.label)}</label>`).join('');
}

/** The host's frames changed (a scene joined or left the Studio's series). */
export function refreshSheetFrames(map: maplibregl.Map): void {
  const st = states.get(map);
  if (!st?.pod) return;
  renderFrames(st);
  renderExportNote(st);
}

/** Which frames an export would make, or why it cannot. */
function exportPlan(st: HostState): { keys: (string | null)[]; problem: string | null } {
  const host = st.host;
  let dates: (string | null)[] = [];
  if (host.timeFrames && st.asof.trim()) {
    try {
      dates = parseAsof(st.asof);
    } catch (err) {
      return { keys: [], problem: err instanceof Error ? err.message : 'Unreadable date.' };
    }
  }
  if (st.area === 'frame' && !st.sheet?.frame) st.area = unframedArea(st);
  if (st.format === 'png') return { keys: [dates[0] ?? null], problem: null };
  if (host.timeFrames) {
    return dates.length >= 2 ? { keys: dates, problem: null }
      : { keys: [], problem: 'List two or more as-of dates to animate, e.g. 2026-06-01, now.' };
  }
  const keys = host.frames().filter((f) => frameIsOn(st, f)).map((f) => f.key);
  return keys.length >= 2 ? { keys, problem: null }
    : { keys: [], problem: 'Pick at least two frames to animate.' };
}

function renderExportNote(st: HostState, working?: string): void {
  const pod = st.pod;
  if (!pod) return;
  const note = pod.querySelector('[data-sh="exnote"]') as HTMLElement;
  const go = pod.querySelector('[data-sh="export"]') as HTMLButtonElement;
  if (working || st.exporting) {
    note.textContent = working ?? 'Working…';
    go.disabled = true;
    return;
  }
  const plan = exportPlan(st);
  go.disabled = !!plan.problem;
  if (plan.problem) { note.textContent = plan.problem; return; }
  const what = st.area === 'frame' ? 'the frame'
    : st.area === 'whole' ? `the ${wholeWord(st).toLowerCase()}` : 'the view';
  note.textContent = st.format === 'gif'
    ? `${plan.keys.length} frames of ${what}, long edge ≤ ${GIF_MAX_EDGE}px`
    : `${what[0]!.toUpperCase()}${what.slice(1)}${plan.keys[0] ? ', as of the date given' : ''}`;
}

// --- templates ---------------------------------------------------------------------

function applyTemplate(st: HostState, spec: TemplateSpec): void {
  if (!st.sheet) return;
  const applied = applyTemplateSpec(spec, st.sheet, { aspect: st.aspect, format: st.format, fps: st.fps });
  st.sheet = applied.sheet;
  st.aspect = applied.session.aspect;
  st.format = applied.session.format;
  st.fps = applied.session.fps;
  if (spec.layers) st.host.applyLayers(spec.layers);
  if (st.sheet.frame && spec.aspect) reshapeFrame(st);   // saves the frame too
  else queueSave(st, true);
  if (st.sheet.id && spec.layers) void flushSave(st, { layers: spec.layers });
  renderPod(st);
  paintMask(st);
}

async function saveTemplate(st: HostState): Promise<void> {
  if (!st.sheet) return;
  const name = prompt('Template name:', st.sheet.title)?.trim();
  if (!name) return;
  const spec = templateSpecOf(st.sheet, { aspect: st.aspect, format: st.format, fps: st.fps });
  const surface: TemplateSurface = spec.layers ? st.host.surface : 'any';
  try {
    const made = await post<TemplateDto>('/api/sheet-templates', { name, surface, spec });
    st.templates = [...st.templates, { ...made, spec: normalizeSpec(made.spec) }]
      .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }));
    renderTemplates(st);
    const sel = st.pod?.querySelector('[data-sh="tpl"]') as HTMLSelectElement | null;
    if (sel) { sel.value = made.id; renderTemplates(st); }
    toast(spec.layers
      ? `Template “${name}” saved with this sheet’s layers — it applies on the ${st.host.surface === 'studio' ? 'Studio' : 'map'} only.`
      : `Template “${name}” saved.`);
  } catch (err) {
    toast(err instanceof Error ? err.message : 'Could not save the template.');
  }
}

async function deleteTemplate(st: HostState): Promise<void> {
  const t = pickedTemplate(st);
  if (!t || !confirm(`Delete the template “${t.name}”?`)) return;
  try {
    await del(`/api/sheet-templates/${t.id}`);
  } catch (err) {
    toast(err instanceof Error ? err.message : 'Could not delete the template.');
    return;
  }
  st.templates = st.templates.filter((x) => x.id !== t.id);
  renderTemplates(st);
  toast('Template deleted.');
}

// --- copy / delete -----------------------------------------------------------------

async function copySheet(st: HostState): Promise<void> {
  const id = st.sheet?.id;
  if (!id) return;
  try {
    const copy = await post<SheetDto>(`/api/sheets/${id}/copy`, {});
    st.list = [copy, ...st.list];
    await useSheet(st.host.map, copy);
    toast(`Copied — now working on “${copy.title}”.`);
  } catch (err) {
    toast(err instanceof Error ? err.message : 'Could not copy the sheet.');
  }
}

async function deleteSheet(st: HostState): Promise<void> {
  const s = st.sheet;
  if (!s?.id) return;
  if (!confirm(`Delete “${s.title}” and everything drawn on it? This cannot be undone.`)) return;
  try {
    await del(`/api/sheets/${s.id}`);
  } catch (err) {
    toast(err instanceof Error ? err.message : 'Could not delete the sheet.');
    return;
  }
  st.list = st.list.filter((x) => x.id !== s.id);
  forget(activeKey(st.host.surface));
  if (st.host.surface === 'studio') {
    startDraft(st.host.map);
  } else {
    st.sheet = null;
    await bindMarkupSheet(st.host.map, null);
    closeSheetPod(st.host.map);
  }
  refreshMarkupRail();
  toast('Sheet deleted.');
}

// --- export ------------------------------------------------------------------------

function viewBbox(map: maplibregl.Map): Bbox {
  const b = map.getBounds();
  return [b.getWest(), b.getSouth(), b.getEast(), b.getNorth()];
}

async function runExport(st: HostState): Promise<void> {
  const sheet = st.sheet;
  if (st.exporting || !sheet) return;
  const plan = exportPlan(st);
  if (plan.problem) { toast(plan.problem); return; }
  const host = st.host;
  const map = host.map;
  const decor = sheet.decor;
  const bbox: Bbox | null = st.area === 'frame' ? sheet.frame?.bbox ?? null
    : st.area === 'whole' ? host.wholeArea?.() ?? null : null;
  st.exporting = true;
  renderExportNote(st, 'Working…');
  // Close a drag in progress: a frame staged under a half-drawn band is not
  // what anyone meant to export.
  st.drawing?.();
  const camera = { center: map.getCenter(), zoom: map.getZoom(), bearing: map.getBearing(),
                   pitch: map.getPitch() };
  const canvases: HTMLCanvasElement[] = [];
  let clipped = false;
  try {
    if (bbox) {
      map.fitBounds([[bbox[0], bbox[1]], [bbox[2], bbox[3]]], {
        padding: 24, animate: false,
        bearing: st.area === 'frame' && sheet.frame ? sheet.frame.bearing : map.getBearing(),
      });
      await mapSettled(map);
    }
    const container = map.getContainer();
    const cssW = container.clientWidth;
    const cssH = container.clientHeight;
    const canvas = map.getCanvas();
    const rect: Rect = bbox ? screenRectOf((ll) => map.project(ll), bbox)
      : { x: 0, y: 0, w: cssW, h: cssH };
    const cut = deviceCrop(rect, cssW, cssH, canvas.width, canvas.height);
    clipped = cut.clipped;
    const shrink = st.format === 'gif' ? Math.min(1, GIF_MAX_EDGE / Math.max(cut.crop.w, cut.crop.h)) : 1;
    const outW = Math.max(64, Math.round(cut.crop.w * shrink));
    const outH = Math.max(64, Math.round(cut.crop.h * shrink));
    // A scale bar on a tilted map would be true along one line only.
    const metersPerPx = map.getPitch() > 5 ? null
      : metersPerCssPx(map.getZoom(), map.getCenter().lat) * (cssW / canvas.width) * (cut.crop.w / outW);
    const legend = decor.legend ? mergeLegend(decor.entries, host.legend(bbox ?? viewBbox(map))) : [];
    const bearing = map.getBearing();
    // Where the image is: its own four corners, unprojected, so a rotated
    // frame still states the ground it shows.
    const coords = decor.coords
      ? coordLine([[rect.x, rect.y], [rect.x + rect.w, rect.y], [rect.x, rect.y + rect.h],
                   [rect.x + rect.w, rect.y + rect.h]].map((pt) => map.unproject(pt as [number, number])),
                  map.unproject([rect.x + rect.w / 2, rect.y + rect.h / 2]),
                  map.getPitch() > 5)
      : null;
    for (const key of plan.keys) {
      await host.stage(key);
      await mapSettled(map);
      const image = await captureMap(map, cut.crop, outW, outH);
      canvases.push(composeSheet(image, {
        title: decor.title ? sheet.title : null,
        subtitle: decor.title ? sheet.caption : null,
        footer: host.footer(key),
        coords,
        legend,
        metersPerPx: decor.scale ? metersPerPx : null,
        northBearing: decor.northArrow ? bearing : null,
      }));
    }
  } catch (err) {
    toast(err instanceof Error ? err.message : 'Export failed.');
    canvases.length = 0;
  } finally {
    try { await host.unstage(); } catch { /* the camera is still put back */ }
    if (bbox) map.jumpTo(camera);
    st.exporting = false;
    renderPod(st);
  }
  if (!canvases.length) return;
  if (clipped) toast('Part of the frame was off screen — the export is cut to what was visible.');
  const name = fileSlug(sheet.title || 'map');
  if (st.format === 'gif') await saveGif(canvases, st.fps, name);
  else await savePng(canvases[0]!, name);
}
